import { wsconnect } from '@nats-io/nats-core';
import { BaseService } from './BaseService';
import { AuthenticationService } from './AuthenticationService';
import { createSingleton } from './singleton';
import { AppConfig } from './AppConfig';
import debug from 'debug';
const log = debug('core:RtService:log');
const error = debug('core:RtService:error');
const warn = debug('core:RtService:warn');
const TE = new TextEncoder();
const TD = new TextDecoder();
function JSONCodec(reviver) {
    return {
        encode(d) {
            if (d === undefined) {
                //                                           
                d = null;
            }
            return TE.encode(JSON.stringify(d));
        },
        decode(a) {
            return JSON.parse(TD.decode(a), reviver);
        },
    };
}
/* 
                         
 */
class Channel {
    _channel;
    _subscriptions = {};
    _connection = null;
    _rSocketSubscriptions = {};
    constructor(channel) {
        this._channel = channel;
    }
    onConnected(connection) {
        this._connection = connection;
        Object.keys(this._subscriptions).forEach((key) => {
            const subject = `bi.${this._channel}.${key}`;
            this._subscribeSubject(subject, args => this._onReceivedMessage(key, args));
        });
    }
    async _subscribeSubject(subject, callback) {
        if (this._rSocketSubscriptions[subject]) {
            error('Already has subscription on subject: ' + String(subject));
            return;
        }
        const sub = await this._connection.subscribe(subject);
        this._rSocketSubscriptions[subject] = sub;
        (async () => {
            for await (const m of sub) {
                try {
                    callback(JSONCodec().decode(m.data));
                }
                catch (err) {
                    error(err);
                }
            }
            this._rSocketSubscriptions[subject] = null;
        })();
        sub.closed.then((err) => {
            if (err) {
                error(`${subject} subscription closed with an error: ${err.message}`);
            }
        });
    }
    subscribe(key, callback) {
        const subject = `bi.${this._channel}.${key}`;
        if (key in this._subscriptions) { //                   
            this._subscriptions[key].push(callback);
        }
        else { //                 
            this._subscriptions[key] = [callback];
            if (this._connection) {
                this._subscribeSubject(subject, args => this._onReceivedMessage(key, args));
            }
        }
        return {
            dispose: () => this.unsubscribe(key, callback),
        };
    }
    unsubscribe(key, callback) {
        if (!(key in this._subscriptions)) {
            return false;
        }
        const idx = this._subscriptions[key].indexOf(callback);
        if (idx === -1) {
            return false;
        }
        const subject = `bi.rt.${key}`;
        this._subscriptions[key].splice(idx, 1);
        if (this._subscriptions[key].length === 0) { //                            
            delete this._subscriptions[key];
            if (this._rSocketSubscriptions[subject]) {
                this._rSocketSubscriptions[subject].drain();
                delete this._rSocketSubscriptions[subject];
            }
        }
        return true;
    }
    publish(key, type, payload) {
        if (!this._connection) {
            //              
            return;
        }
        const subject = `bi.${this._channel}.${key}`;
        this._connection.publish(subject, JSONCodec().encode([{ type, payload }]));
    }
    _onReceivedMessage(key, messages) {
        if (key in this._subscriptions) {
            messages.forEach((command) => {
                const { type, payload } = command;
                this._notify(key, command);
            });
        }
    }
    _notify(key, command) {
        const { type, payload } = command;
        const callbacks = this._subscriptions[key];
        if (!callbacks) {
            return;
        }
        callbacks.forEach((callback) => {
            try {
                callback(type, payload);
            }
            catch (err) {
                error('Error notifying callback', err);
            }
        });
    }
}
export class RtService extends BaseService {
    _connection = null;
    _channels;
    constructor() {
        super();
        this._channels = {
            'rt': new Channel('rt'),
            'cube': new Channel('cube'),
        };
        this._setModel({
            loading: true,
            error: null,
            connected: false,
        });
        AuthenticationService.subscribeUpdatesAndNotify(() => this._reload());
    }
    subscribeDataset(schema_name, callback) {
        return this._channels.rt.subscribe(schema_name, callback);
    }
    unsubscribeDataset(schema_name, callback) {
        return this._channels.rt.unsubscribe(schema_name, callback);
    }
    publishDsMessage(schema_name, type, payload) {
        return this._channels.rt.publish(schema_name, type, payload);
    }
    subscribeCube(cube, callback) {
        return this._channels.cube.subscribe(cube, callback);
    }
    unsubscribeCube(schema_name, callback) {
        return this._channels.cube.unsubscribe(schema_name, callback);
    }
    publishCubeMessage(cube, type, payload) {
        return this._channels.cube.publish(cube, type, payload);
    }
    /* 
                  
     */
    onInternalDataUpdated(schemaName, mlpvPtrs) {
        this.publishDsMessage(schemaName, 'ADD_DATAS', mlpvPtrs);
    }
    async _reload() {
        const authModel = AuthenticationService.getModel();
        if (authModel.error) {
            this._disconnect();
            this._setModel({ connected: false, loading: false, error: authModel.error });
            return;
        }
        if (authModel.loading) {
            this._setModel({ loading: true, error: null, connected: false });
            return;
        }
        if (!authModel.authenticated) {
            this._disconnect();
            this._setModel({
                loading: false,
                error: AuthenticationService.NOT_AUTHENTICATED,
                connected: false,
            });
            return;
        }
        //                 
        try {
            if (!this._connection) {
                await this._connect();
            }
        }
        catch (err) {
            error(err);
        }
    }
    _onConnected = () => {
        this._channels.rt.onConnected(this._connection);
        this._channels.cube.onConnected(this._connection);
    };
    _getUrl() {
        let url = AppConfig.fixRequestUrl('/srv/bI/');
        if (url.startsWith('ws://') || url.startsWith('wss://')) {
            //                                         
        }
        else if (url[0] === '/') {
            url = window.location.protocol + '//' + window.location.host + url;
        }
        else {
            url =
                window.location.protocol +
                    '//' +
                    window.location.host +
                    window.location.pathname.replace(/[^/]*$/, '') +
                    url.replace(/^\.\//, '');
        }
        url = url.replace(/^http/, 'ws');
        return url;
    }
    async _connect() {
        this._connection = await wsconnect({ servers: [this._getUrl()] });
        this._onConnected();
        if (typeof window !== 'undefined') {
            window.__nats = this._connection;
        }
        this._setModel({
            connected: true,
            error: null,
            loading: false,
        });
    }
    _disconnect() {
        //
    }
    static getInstance = createSingleton(() => new RtService(), '__rtService');
    static publishDsMessage(schemaName, type, payload) {
        this.getInstance().publishDsMessage(schemaName, type, payload);
    }
}
